/**
 * ALOHA PLATFORM
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const AUTH_URL = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [usageInfo, setUsageInfo] = useState(null);
  const [processingOAuth, setProcessingOAuth] = useState(false);

  // Check for OAuth session_id in URL fragment on mount
  useEffect(() => {
    const checkOAuthCallback = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        setProcessingOAuth(true);
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          // Process OAuth session
          const response = await axios.post(
            `${API}/auth/google/session`,
            { session_id: sessionId },
            { withCredentials: true }
          );
          
          setUser(response.data.user);
          
          // Clear hash from URL
          window.history.replaceState(null, '', window.location.pathname);
          
          setProcessingOAuth(false);
          setLoading(false);
        } catch (error) {
          console.error('OAuth processing failed:', error);
          setProcessingOAuth(false);
          setLoading(false);
        }
      } else if (token) {
        fetchUser();
      } else {
        // Check if session_token cookie exists (will be handled by backend)
        fetchUser().catch(() => setLoading(false));
      }
    };
    
    checkOAuthCallback();
  }, []);

  useEffect(() => {
    if (user) {
      fetchUsageInfo();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const config = {};
      if (token) {
        config.headers = { Authorization: `Bearer ${token}` };
      }
      config.withCredentials = true; // Include cookies for session_token
      
      const response = await axios.get(`${API}/auth/me`, config);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (token) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/usage`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setUsageInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch usage info:', error);
    }
  };

  const loginWithGoogle = () => {
    // Redirect to Emergent OAuth
    const redirectUrl = encodeURIComponent(`${window.location.origin}/dashboard`);
    window.location.href = `${AUTH_URL}/?redirect=${redirectUrl}`;
  };

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      // Store token
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      
      // Remember email if "Stay Logged In" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
        localStorage.setItem('stayLoggedIn', 'true');
      } else {
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('stayLoggedIn');
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const signup = async (email, password, fullName) => {
    try {
      await axios.post(`${API}/auth/signup`, {
        email,
        password,
        full_name: fullName
      });
      
      // Auto-login after signup
      return await login(email, password);
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Signup failed' 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { 
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUsageInfo(null);
  };

  const value = {
    user,
    loading,
    token,
    login,
    signup,
    logout,
    loginWithGoogle,
    isAuthenticated: !!user,
    usageInfo,
    fetchUsageInfo,
    processingOAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
